import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
  forwardRef,
} from "react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import * as d3 from "d3";
import Request from "common/Request";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import { useDimensions } from "common/hooks";
import {
  expressTarget,
  doSystemDateRetrieval,
  parseSystemDate,
  calcRangeExtent,
  isInsideRange,
  getBqnIdCmdPart,
  safeSubsId,
  csvName,
} from "common/api";
import RangeSliderInput from "common/RangeSliderInput";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import ScatterPlot from "common/graphs/ScatterPlot/ScatterPlot";
import { TileContainer } from "common/graphs/FacetGraph";
import FacetGraph from "common/graphs/ConnectedFacetGraph";
import {
  parseSubscriberFlows,
  calcFlowsCreationPerMinute,
  parseSubscriberMaxSpeeds,
  parseSubscriberLatencies,
  parseSubscriberRetransmissions,
  parseTrafficCongestion,
  parseLatencyReduction,
  parseGroupsResponse,
} from "./api";
import { toObjectLiteral } from "common/utils";
import BqnContext from "common/BqnContext";
import {
  metricChoices as allMetricChoices,
  hoursAsPeriod, periodAsHours, hoursScopeChoices
} from "common/constants";
import Switch from "@mui/material/Switch";
import { getThresholds, metricThresholdMap } from "views/Status/Subscriber&Groups/utils";

const Wrapper = styled.div`
  .historical-metrics-content {
    .hasExtraBoldLine {
      .graph .nvd3-svg {
        g.nv-groups {
          g.nv-series-0 {
            stroke-width: 4 !important;
            path {
              stroke-width: 4 !important;
            }
          }
        }
      }
    }
  }
`;

const ControlsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row wrap;
  margin: 0 0 20px 0;
  align-items: center;
  justify-content: space-between;
  .controls {
    flex-basis: auto;
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    justify-content: flex-end;
  }
`;

const ContextMenuDiv = styled.div`
  display: flex;
  align-items: center;

  i {
    color: #999;
    font-size: 20px;

    &:hover {
      color: #000;
    }
  }
`;

const H4Centered = styled.h4`
  font-size: 18px;
  margin-top: 7px;
  margin-bottom: 7px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  h4.chart-title {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
`;

const SelectWrapper = styled.div`
  width: 180px;
  margin-bottom: 0px;
`;

const WrapperSwitch = styled.div`
  display: flex;
  align-items: center;
  .sw-input.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }
`;

export const metricChoices = [
  { value: "Rtt-80%-Dn", label: "P80-80th percentile" },
  { value: "Rtt-Med-Dn", label: "P50-Median" },
  { value: "Rtt-Min-Dn", label: "Mean-Min" },
];

const doesNothing = () => {};
const MARGIN_PLOT = { top: 0, right: 40, bottom: 50, left: 60 };
const PADDING_CHART_CONTAINER = 30;
const noIPv6Mask = { ipv6Mask: false };
const metricLatencyTooltipMap = {
  "Rtt-80%-Dn":
    "The 80-th percentile of all downlink RTT latency, measured from BQN to end user, in 10 minutes.",
  "Rtt-Med-Dn":
    "The 50-th percentile (median) of all downlink RTT latency samples, measured from BQN to end user, in 5 minutes.",
  "Rtt-Min-Dn":
    "The mean of all downlink RTT minimum latency samples, measured in 1-second intervals, during active downloads, in 5 minutes.",
};
const getLatencyItems = ({ metric, down, med, eight }) => {
  if (metric === "Rtt-80%-Dn") {
    return eight;
  }
  if (metric === "Rtt-Med-Dn") {
    return med;
  }
  return down;
};

const getFieldsLatency = (groups, target, metric) => {
  const fieldMain = [
    { name: target, label: target, tooltip: metricLatencyTooltipMap[metric] },
  ];
  return [
    ...fieldMain,
    ...groups.map((group) => {
      if (group === "all-subscribers") {
        return {
          name: "all-subscribers",
          label: "All Subscribers",
          tooltip: metricLatencyTooltipMap[metric]
        };
      }

      return {
        name: group,
        label: group,
        tooltip: metricLatencyTooltipMap[metric]
      };
    }),
  ];
};

const getFieldTarget = (target) => {
  const { addr, subsId } = target;
  if (addr) {
    return addr;
  }
  if (subsId) {
    return subsId;
  }
  return null;
};

export const doStatsRetrieval = ({
  target = null,
  facet = "volume",
  hours = 24,
  interval = 10,
  bqnId = null,
}) => {
  return expressTarget(target, noIPv6Mask).then((expressedTarget) => {
    if (facet.startsWith("latency-") && !target.addr) {
      const [, id] = expressedTarget.split(" ");
      return ifCl.run(
        `show statistics subscribers ${facet} id '${csvName(target.subsId)}' hours ${hours} interval ${interval}`
      );
    }
    return ifCl.run(
      `show statistics subscribers ${facet} ${expressedTarget} hours ${hours} interval ${interval}${
        bqnId !== null ? ` bqn-id ${bqnId}` : ""
      }`
    );
  });
};

const systemTimeRetrieval = () => doSystemDateRetrieval().then(parseSystemDate);

const SubcriberDetailsContextControls = ({
  range,
  onChange = doesNothing,
  children,
}) => (
    <>
      {children}
      <TileContainer className="full-width can-compress no-print margin-20 margin-t-0">
        <RangeSliderInput {...range} onChange={onChange} />
      </TileContainer>
    </>
  );

const parseAndCalcFlowsCreationPerMinute = (response) =>
  calcFlowsCreationPerMinute(parseSubscriberFlows(response));

const scatterItemsXSpeed = (yItems, speedItems, yLabel) => {
  if (!yItems || yItems.length === 0) {
    return [];
  }

  return speedItems.reduce((acc, item, index) => {
    const { downMbPerSec, time } = item;
    if (yItems[index] && yItems[index][yLabel] !== null && downMbPerSec !== null)
      acc.push({ y: yItems[index][yLabel], x: downMbPerSec, time });
    return acc;
  }, [])
};

const formatDate = d3.timeFormat("%m/%d %H:%M");

function getTimeValuesMaps(itemsCharts) {
  return Object.keys(itemsCharts).reduce((acc, itemsChart) => {
    const mapKey = `${itemsChart}Map`;
    const items = itemsCharts[itemsChart];
    if (items && items.length === 0) {
      acc[mapKey] = {};
    }
    const mapValues = items.reduce((acc, value) => {
      const { time } = value;
      const timeKey = formatDate(time);
      acc[timeKey] = value;
      return acc;
    }, {});
    acc[mapKey] = mapValues;
    return acc;
  }, {});
}

const ScatterPlotLatencySpeed = forwardRef(
  ({ items, interactionData, onInteractionData, metric, setMetric, warning }, ref) => {
    return (
      <TileContainer className="chart-container" ref={ref}>
        <Header>
          <h4 className="chart-title lowercase">Latency vs Average Speed</h4>
          <SelectWrapper>
            <SelectInputWithIcon
              title="Metric"
              name="Metric"
              icon="insert_chart"
              selected={metric}
              onChange={({ target }) => setMetric(target.value)}
              options={metricChoices}
            />
          </SelectWrapper>
        </Header>
        <div className="graph">
          <ScatterPlot
            items={items}
            contentY={{
              yAxisLabel: "Access latency (ms)",
              tooltipLabel: "Latency",
              tooltipUnit: "ms",
            }}
            contentX={{
              xAxisLabel: "Average downlink speed (Mbps)",
              tooltipLabel: "Average Speed",
              tooltipUnit: "Mbps",
            }}
            interactionData={interactionData}
            onInteractionData={onInteractionData}
            key={`latency-speed-scatter-plot-${metric}`}
            warning={warning}
          />
        </div>
      </TileContainer>
    );
  }
);

ScatterPlotLatencySpeed.displayName = "ScatterPlotLatencySpeed";

const SubscriberAnalyticsResult = ({
  target,
  range: initialRange,
  itemsSpeed,
  itemsMaxSpeed,
  itemsLatencyDown,
  itemsLatencyMed,
  itemsLatency80,
  itemsLatencyDownGroups,
  itemsLatencyMedGroups,
  itemsLatency80Groups,
  itemsRetransmission,
  itemsFlows,
  itemsCongestionLatency,
  itemsCongestionTraffic,
  groups = [],
  hours = 24,
  onHoursChange = doesNothing,
  metric,
  setMetric,
  warningEnabled,
  setWarningEnabled,
}) => {
  const [thresholds, setThresholds] = useState({});
  const scatterPlotRef = useRef();
  const dimensions = useDimensions(scatterPlotRef);
  const widthChart =
    dimensions.width -
    MARGIN_PLOT.left -
    MARGIN_PLOT.right -
    PADDING_CHART_CONTAINER;
  const [range, setRange] = useState(initialRange);
  const applyRange = (newRange) => setRange(newRange);
  const [interactionData, setInteractionData] = useState(null);
  const applyChange = ({ hours, ...range }) => {
    if (hours !== undefined) {
      onHoursChange(hours);
    } else {
      applyRange(range);
    }
  };
  useEffect(()=> {
    getThresholds().then((response)=> {
      setThresholds(response);
    })
  }, []);

  const itemsLatencySelected = useMemo(() => {
    return getLatencyItems({
      metric,
      down: itemsLatencyDown,
      med: itemsLatencyMed,
      eight: itemsLatency80,
    });
  }, [metric, itemsLatencyDown, itemsLatencyMed, itemsLatency80]);

  const itemsLatencyGroups = useMemo(() => {
    return getLatencyItems({
      metric,
      down: itemsLatencyDownGroups,
      med: itemsLatencyMedGroups,
      eight: itemsLatency80Groups,
    });
  }, [
    metric,
    itemsLatencyDownGroups,
    itemsLatencyMedGroups,
    itemsLatency80Groups,
  ]);

  const itemsLatency = itemsLatencySelected.map((item, index) => {
    return { ...item, ...(itemsLatencyGroups ? itemsLatencyGroups[index] : {}) };
  });

  const {
    itemsSpeedMap,
    itemsLatencyMap,
    itemsFlowsMap,
    itemsRetransmissionMap,
    itemsMaxSpeedMap,
    itemsCongestionLatencyMap,
    itemsCongestionTrafficMap,
  } = useMemo(() => {
    return getTimeValuesMaps({
      itemsSpeed,
      itemsLatency,
      itemsFlows,
      itemsRetransmission,
      itemsMaxSpeed,
      itemsCongestionLatency,
      itemsCongestionTraffic,
    });
  }, [
    itemsSpeed,
    itemsLatency,
    itemsFlows,
    itemsRetransmission,
    itemsMaxSpeed,
    itemsCongestionLatency,
    itemsCongestionTraffic,
  ]);

  if (itemsSpeed.length === 0) {
    return;
  }

  const xScale = useMemo(() => {
    return d3
      .scaleTime()
      .range([0, widthChart])
      .domain([itemsSpeed[0].time, itemsSpeed[itemsSpeed.length - 1].time]);
  }, [itemsSpeed, widthChart]);

  const fieldTarget = getFieldTarget(target);

  return (
    <Wrapper>
      <SubcriberDetailsContextControls
        target={target}
        range={initialRange}
        hours={hours}
        onChange={applyChange}
        warningEnabled={warningEnabled}
        setWarningEnabled={setWarningEnabled}
      >
      <ColumnsGrid
        gap="1rem"
        columns={2}
        minWidth="9cm"
        className="historical-metrics-content"
      >
          <FacetGraph
            title="Average Speed Over Time"
            items={itemsSpeed.filter(isInsideRange(range))}
            facet="speed"
            yAxisUnits="Mbps"
            fields={[
              { name: "downMbPerSec", label: "Down-Mbps" },
              { name: "upMbPerSec", label: "Up-Mbps" },
            ]}
            onInteractionData={setInteractionData}
            interactionData={interactionData}
            valuesMap={itemsSpeedMap}
            xScale={xScale}
            widthChart={widthChart}
            hours={hours}
          />
          <FacetGraph
            title="Maximum Speed Over Time"
            items={itemsMaxSpeed.filter(isInsideRange(range))}
            facet="max-speed"
            yAxisUnits="Mbps"
            fields={[
              { name: "downMbPerSec", label: "Down-Mbps" },
              { name: "upMbPerSec", label: "Up-Mbps" },
            ]}
            onInteractionData={setInteractionData}
            interactionData={interactionData}
            valuesMap={itemsMaxSpeedMap}
            xScale={xScale}
            widthChart={widthChart}
            hours={hours}
          />
          <FacetGraph
            title="Latency Over Time"
            items={itemsLatency.filter(isInsideRange(range))}
            facet="latency"
            yAxisUnits="milliseconds"
            fields={getFieldsLatency(groups, fieldTarget, metric)}
            onInteractionData={setInteractionData}
            interactionData={interactionData}
            valuesMap={itemsLatencyMap}
            xScale={xScale}
            widthChart={widthChart}
            hours={hours}
            className="hasExtraBoldLine"
            metric={metric}
            setMetric={setMetric}
            warning={warningEnabled ? thresholds[metricThresholdMap[metric]] : undefined}
            metricChoices={metricChoices}
          />
          <FacetGraph
            title="Packet retransmissions Over Time"
            items={itemsRetransmission.filter(isInsideRange(range))}
            facet="retransmission"
            yAxisUnits="% retransmisions"
            fields={[
              { name: "subsAccessRtx", label: target.subsId || target.addr },
              ...groups.map((group) => {
                if (group === "all-subscribers") {
                  return {
                    name: "all-subscribers",
                    label: "All Subscribers",
                  };
                }

                return {
                  name: group,
                  label: group,
                };
              }),
            ]}
            onInteractionData={setInteractionData}
            interactionData={interactionData}
            valuesMap={itemsRetransmissionMap}
            xScale={xScale}
            widthChart={widthChart}
            hours={hours}
            className="hasExtraBoldLine"
            warning={warningEnabled ? thresholds["RTX"] : undefined}
          />
          <FacetGraph
            title="Average traffic at high speed and under congestion Over Time"
            items={itemsCongestionTraffic.filter(isInsideRange(range))}
            facet="congestion-total"
            yAxisUnits="% of total traffic"
            fields={[
              { name: "maxSpeed", label: "Max-speed-traffic-%" },
              { name: "congestion", label: "Congested-traffic-%" },
              { name: "acm", label: "ACM-%" },
            ]}
            onInteractionData={setInteractionData}
            interactionData={interactionData}
            valuesMap={itemsCongestionTrafficMap}
            xScale={xScale}
            widthChart={widthChart}
            hours={hours}
          />
          <FacetGraph
            title="Flows Over Time"
            items={itemsFlows.filter(isInsideRange(range))}
            facet="flows"
            yAxisUnits="Flows"
            fields={[
              { name: "flCreatedPerMinute", label: "Fl-Created-per-minute" },
              { name: "flActive", label: "Fl-Active" },
            ]}
            onInteractionData={setInteractionData}
            interactionData={interactionData}
            valuesMap={itemsFlowsMap}
            xScale={xScale}
            widthChart={widthChart}
            hours={hours}
          />
          <ScatterPlotLatencySpeed
            items={scatterItemsXSpeed(
              itemsLatencySelected,
              itemsSpeed,
              fieldTarget
            ).filter(isInsideRange(range))}
            interactionData={interactionData}
            onInteractionData={setInteractionData}
            ref={scatterPlotRef}
            metric={metric}
            setMetric={setMetric}
            warning={warningEnabled && metric === "Rtt-Min-Dn"? thresholds["RTT-ms"] : undefined}
          />
          <TileContainer className="chart-container">
            <h4 className="chart-title lowercase">
              Packet Retransmissions vs Average Speed
            </h4>
            <div className="graph">
              <ScatterPlot
                items={scatterItemsXSpeed(
                  itemsRetransmission,
                  itemsSpeed,
                  "subsAccessRtx"
                ).filter(isInsideRange(range))}
                contentY={{
                  yAxisLabel: "Retransmissions (%)",
                  tooltipLabel: "Retransmissions",
                  tooltipUnit: "%",
                }}
                contentX={{
                  xAxisLabel: "Average downlink speed (Mbps)",
                  tooltipLabel: "Average Speed",
                  tooltipUnit: "Mbps",
                }}
                interactionData={interactionData}
                onInteractionData={setInteractionData}
                warning={warningEnabled ? thresholds["RTX"] : undefined}
              />
            </div>
          </TileContainer>
          <TileContainer className="chart-container">
            <h4 className="chart-title lowercase">
              Congestion vs Average Speed
            </h4>
            <div className="graph">
              <ScatterPlot
                items={scatterItemsXSpeed(
                  itemsCongestionTraffic,
                  itemsSpeed,
                  "congestion"
                ).filter(isInsideRange(range))}
                contentY={{
                  yAxisLabel: "Congested traffic (%)",
                  tooltipLabel: "Congestion",
                  tooltipUnit: "%",
                }}
                contentX={{
                  xAxisLabel: "Average downlink speed (Mbps)",
                  tooltipLabel: "Average Speed",
                  tooltipUnit: "Mbps",
                }}
                interactionData={interactionData}
                onInteractionData={setInteractionData}
                warning={warningEnabled ? thresholds["CONGEST"] : undefined}
              />
            </div>
          </TileContainer>
      </ColumnsGrid>
      </SubcriberDetailsContextControls>
    </Wrapper>
  );
};

const promisesInfo = [
  { facet: "speed", parse: parseSubscriberMaxSpeeds },
  { facet: "max-speed", parse: parseSubscriberMaxSpeeds },
  { facet: "latency-down", parse: parseSubscriberLatencies },
  { facet: "latency-percentile-med", parse: parseSubscriberLatencies },
  { facet: "latency-percentile-80p", parse: parseSubscriberLatencies },
  { facet: "retransmission", parse: parseSubscriberRetransmissions },
  { facet: "flows", parse: parseAndCalcFlowsCreationPerMinute },
  { facet: "congestion", parse: parseLatencyReduction },
  { facet: "congestion", parse: parseTrafficCongestion },
];

const promises = ({
  target,
  hours,
  interval,
  ipAddressSelected,
  bqnId,
  mode,
}) => {
  const promisesStats = promisesInfo.map(({ facet, parse }) => {
    return doStatsRetrieval({
      facet,
      target,
      hours,
      interval,
      bqnId: mode === "cluster" && bqnId !== "all" ? bqnId : null,
    })
      .then(parse)
      .catch((error) => console.error(error) || []);
  });
  return Promise.all([
    ...promisesStats,
    fetchSubscriberGroupsItems({
      target,
      hours,
      interval,
      ipAddressSelected,
      mode,
      bqnId,
    }),
  ]);
};

const promisesGroupsKeys = [
  "latency-down",
  "latency-percentile-80p",
  "latency-percentile-med",
  "retransmission-down",
];

const getIpAddress = ({ block, mode, bqnId }) => {
  const rows = block.trim().split("\n");
  if (mode === "standalone") {
    const [label, ipAddressRaw] = rows[0].split(":");
    return ipAddressRaw.trim();
  }
  const [, ip, , bqn] = rows[0].split(/\s{2,}/);

  if (bqnId === "all" || parseFloat(bqn) === parseFloat(bqnId)) {
    return ip;
  }
}

const getPromiseGroups = ({ target, ipAddressSelected, mode, bqnId }) => {
  const { addr, subsId } = target;
  if (addr) {
    return ifCl
      .run(`show api subscriber ${addr}${getBqnIdCmdPart(mode, bqnId)}`)
      .then((response) => {
        const groupsSet = parseGroupsResponse(response);
        return [...groupsSet];
      });
  }
  return ifCl
    .run(`show subscriber id ${safeSubsId(subsId)} short`)
    .then((response) => {
      const blocks = response.trim().split("\n\n");
      const ipAddresses = blocks.reduce((acc, block) => {
        const ipadrress = getIpAddress({ block, mode, bqnId });
        if (ipadrress) {
          acc.push(ipadrress);
        }
        return acc;
      }, []);
      const ipPromises = [ipAddressSelected].map((ip) => {
        return ifCl
          .run(`show api subscriber ${ip}${getBqnIdCmdPart(mode, bqnId)}`)
          .then((response) => {
            const groupsSet = parseGroupsResponse(response);
            return [...groupsSet];
          });
      });

      return Promise.all(ipPromises);
    })
    .then((responses) => {
      const groupsTotal = responses.flat();
      const groupsSet = new Set(groupsTotal);
      return [...groupsSet];
    });
};

const fetchSubscriberGroupsItems = ({
  target,
  hours,
  interval,
  ipAddressSelected,
  mode,
  bqnId,
}) => {
  return getPromiseGroups({ target, ipAddressSelected, mode, bqnId }).then(
    (groups) => {
      if (!groups || groups.length === 0) {
        return { groups: [], itemsLatency: [], itemsRetransmissions: [] };
      }

      const csvGroups = groups
        .map((group) => csvName(group))
        .join(",");
      const promisesGroups = promisesGroupsKeys.map((key) => {
        return ifCl.run(
          `show statistics subscriber-groups name '${csvGroups}' ${key} hours ${hours} interval ${interval}${getBqnIdCmdPart(
            mode,
            bqnId
          )}`
        );
      });

      return Promise.all(promisesGroups).then(
        ([
          responseLatencyDown,
          responseLatency80,
          responseLatencyMed,
          responseRetransmissions,
        ]) => {
          return {
            groups: groups,
            itemsLatencyDown: parseSubscriberLatencies(responseLatencyDown),
            itemsLatency80: parseSubscriberLatencies(responseLatency80),
            itemsLatencyMed: parseSubscriberLatencies(responseLatencyMed),
            itemsRetransmissions: parseSubscriberRetransmissions(
              responseRetransmissions
            ),
          };
        }
      );
    }
  );
};

const retrieval = ({
  target,
  hours,
  interval,
  ipAddressSelected,
  mode,
  bqnId,
}) => {
  return Promise.all([
    systemTimeRetrieval().then((systemDate) =>
      calcRangeExtent(systemDate, hours)
    ),
    promises({ target, hours, interval, ipAddressSelected, bqnId, mode }),
  ]).then(
    ([
      range,
      [
        itemsSpeed,
        itemsMaxSpeed,
        itemsLatencyDown,
        itemsLatencyMed,
        itemsLatency80,
        itemsRetransmission,
        itemsFlows,
        itemsCongestionLatency,
        itemsCongestionTraffic,
        responseSubscriberGroups,
      ],
    ]) => {
      const {
        itemsLatencyDown: itemsLatencyDownGroups,
        itemsLatencyMed: itemsLatencyMedGroups,
        itemsLatency80: itemsLatency80Groups,
        groups: latencyGroups,
        itemsRetransmissions: itemsRetransmissionsGroups,
      } = responseSubscriberGroups;
      const itemsRetransmissionTotal = itemsRetransmission.map(
        (item, index) => {
          return { ...item, ...(itemsRetransmissionsGroups ? itemsRetransmissionsGroups[index] : {}) };
        }
      );
      return {
        range,
        itemsSpeed,
        itemsMaxSpeed,
        itemsLatencyDown,
        itemsLatencyMed,
        itemsLatency80,
        itemsLatencyDownGroups,
        itemsLatencyMedGroups,
        itemsLatency80Groups,
        itemsRetransmission: itemsRetransmissionTotal,
        itemsFlows,
        itemsCongestionLatency,
        itemsCongestionTraffic,
        groups: latencyGroups,
      };
    }
  );
};


const willUpdateFieldAsNumber = (onChage) =>
  (event) =>
    onChage(parseInt(event.target.value))

const HistoricalMetricsControls = ({
  hours,
  onHoursChange = doesNothing,
  warningEnabled,
  setWarningEnabled,
}) =>
  <>
    <WrapperSwitch>
      <div>
        {!warningEnabled
          ? "Colorize warning threshold: disabled"
          : "Colorize warning threshold: enabled"}
      </div>
      <Switch
        className="sw-input"
        checked={warningEnabled}
        onChange={() => setWarningEnabled(!warningEnabled)}
      />
    </WrapperSwitch>
    <SelectInputWithIcon
      title="Date Range"
      name="Date Range"
      icon="date_range"
      selected={hours || 24}
      onChange={willUpdateFieldAsNumber(onHoursChange)}
      options={hoursScopeChoices}
    />
  </>

const SubscriberAnalyticContextRequest = ({
  target,
  context,
  onHoursChange = doesNothing,
  ipAddressSelected,
  warningEnabled,
  setWarningEnabled,
}) => {
  const [request, setRequest] = useState(null);
  let [searchParams, setSearchParams] = useSearchParams();
  const [metric, setMetric] = useState("Rtt-Min-Dn");
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const doLoad = () => {
    setRequest(
      context.hours
        ? retrieval({ target, ipAddressSelected, bqnId, mode, ...context })
        : null
    );
  };

  const targetStr = target ? (target.addr ? target.addr : target.subsId) : "";
  const contextStr = "" + context.hours + context.interval;

  useEffect(() => {
    if (targetStr && ipAddressSelected) {
      doLoad();
    }
  }, [contextStr, targetStr, ipAddressSelected, bqnId]);

  return request === null ? null : (
    <Request during={request}>
      {(result) => (
          <div className="padding-b-10">
            <ControlsContainer className="header-with-controls">
              <H4Centered className="subtitle"> Historical Metrics </H4Centered>
              <div className="controls">
                <HistoricalMetricsControls
                  hours={context.hours}
                  onHoursChange={onHoursChange}
                  warningEnabled={warningEnabled} setWarningEnabled={setWarningEnabled}
                />
                <ContextMenuDiv className="context-menu">
                  <a
                    onClick={doLoad}
                    data-toggle="cardloading"
                    data-loading-effect="pulse"
                    title="Refresh"
                  >
                    <i className="material-icons">refresh</i>
                  </a>
                </ContextMenuDiv>
              </div>
            </ControlsContainer>
            <SubscriberAnalyticsResult
              {...result}
              {...context}
              target={target}
              onHoursChange={(hours) => {
                onHoursChange(hours);
                setSearchParams({
                  ...toObjectLiteral(searchParams),
                  timePeriod: hoursAsPeriod[hours],
                });
              }}
              metric={metric}
              setMetric={setMetric}
              warningEnabled={warningEnabled}
              setWarningEnabled={setWarningEnabled}
            />
          </div>
      )}
    </Request>
  );
};

const _defaultContext = {
  hours: 24,
  interval: 10,
};

const intervalForHours = (hours, interval = 10) =>
  hours > 7 * 24 ? 60 : interval;

const SubscriberAnalyticContext = ({ target, ipAddressSelected }) => {
  let [searchParams,] = useSearchParams();
  const [warningEnabled, setWarningEnabled] = useState(true);
  const period = searchParams.get("timePeriod");
  const [context, setContext] = useState({
    ..._defaultContext,
    hours: periodAsHours[period] ?? _defaultContext.hours,
  });

  const setHoursInContext = (hours) =>
    setContext({ hours, interval: intervalForHours(hours) });

  return (
    <SubscriberAnalyticContextRequest
      target={target}
      context={context}
      onHoursChange={setHoursInContext}
      ipAddressSelected={ipAddressSelected}
      warningEnabled={warningEnabled}
      setWarningEnabled={setWarningEnabled}
    />
  );
};
export default SubscriberAnalyticContext;
